import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { WorkflowDetalhe, WorkflowEtapa, WorkflowEtapaTipo } from '../../core/models/workflow.models';
import { WorkflowsService } from '../../core/services/workflows.service';

type StatusFilter = 'TODOS' | 'EM_ANDAMENTO' | 'CONCLUIDO' | 'ATRASADO';
type OrderFilter = 'PARADO' | 'ATRASO' | 'VALOR' | 'ANTIGO';

interface ParadoRecord {
  workflow: WorkflowDetalhe;
  osNumero: string;
  veiculo: string;
  etapaAtual: string;
  responsavel: string;
  seguradora: string;
  dataInicio: Date;
  dataFinalPrevista: Date | null;
  dataFinalReal: Date | null;
  ultimaAtividade: Date;
  diasParado: number;
  atrasoDias: number;
  emAtraso: boolean;
  sinais: {
    checklist: boolean;
    comentario: boolean;
    anexo: boolean;
    comunicacao: boolean;
  };
}

interface GroupEtapa {
  etapa: string;
  items: ParadoRecord[];
  parados: number;
  criticos: number;
  maxDias: number;
  mediaDias: number;
  top3: ParadoRecord[];
}

interface GroupResponsavel {
  responsavel: string;
  items: ParadoRecord[];
  parados: number;
  criticos: number;
  etapaTop: string;
}

@Component({
  selector: 'app-relatorios-processo-dias-parado',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './relatorios-processo-dias-parado.component.html',
  styleUrl: './relatorios-processo-dias-parado.component.scss',
})
export class RelatoriosProcessoDiasParadoComponent implements OnInit, OnDestroy {
  workflows: WorkflowDetalhe[] = [];
  baseItems: ParadoRecord[] = [];
  filteredItems: ParadoRecord[] = [];
  paradosItems: ParadoRecord[] = [];

  filterStart = '';
  filterEnd = '';
  filterSeguradora = 'TODOS';
  filterResponsavel = 'TODOS';
  filterEtapa = 'TODAS';
  filterStatus: StatusFilter = 'TODOS';
  onlyWip = true;
  onlyAtraso = false;
  paradoDias = 3;
  orderBy: OrderFilter = 'PARADO';

  seguradoras: string[] = [];
  responsaveis: string[] = [];
  etapas: string[] = [];

  totalAnalisado = 0;
  totalParados = 0;
  paradosCriticos = 0;
  paradosEmAtraso = 0;
  maiorParado = 0;
  mediaParado = 0;

  gruposEtapa: GroupEtapa[] = [];
  gruposResponsavel: GroupResponsavel[] = [];
  selectedResponsavel: string | null = null;

  activeTab: 'lista' | 'etapa' | 'responsavel' = 'lista';

  private subscription = new Subscription();

  constructor(private workflowsService: WorkflowsService, private router: Router) {}

  ngOnInit(): void {
    const today = new Date();
    const endStr = this.toDateInput(today);
    const start = new Date(today);
    start.setDate(start.getDate() - 30);
    this.filterStart = this.toDateInput(start);
    this.filterEnd = endStr;

    this.subscription.add(
      this.workflowsService.getAllDetails().subscribe((workflows) => {
        this.workflows = workflows;
        this.seguradoras = this.buildList((wf) => wf.seguradoraNome);
        this.responsaveis = this.buildList((wf) => wf.responsavelAtual);
        this.etapas = this.buildEtapas(workflows);
        this.baseItems = this.buildRecords(workflows);
        this.applyFilters();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  applyFilters(): void {
    const start = this.filterStart ? new Date(`${this.filterStart}T00:00:00`) : null;
    const end = this.filterEnd ? new Date(`${this.filterEnd}T23:59:59`) : null;
    const now = new Date();

    this.filteredItems = this.baseItems.filter((item) => {
      if (start && item.dataInicio < start) return false;
      if (end && item.dataInicio > end) return false;
      if (this.filterSeguradora !== 'TODOS' && item.seguradora !== this.filterSeguradora) return false;
      if (this.filterResponsavel !== 'TODOS' && item.responsavel !== this.filterResponsavel) return false;
      if (this.filterEtapa !== 'TODAS' && item.etapaAtual !== this.filterEtapa) return false;

      if (this.filterStatus !== 'TODOS') {
        const finalizado = this.isFinalizado(item.workflow);
        if (this.filterStatus === 'CONCLUIDO' && !finalizado) return false;
        if (this.filterStatus === 'EM_ANDAMENTO' && finalizado) return false;
        if (this.filterStatus === 'ATRASADO' && !item.emAtraso) return false;
      }

      if (this.onlyWip && this.isFinalizado(item.workflow)) return false;
      if (this.onlyAtraso && item.atrasoDias <= 0) return false;

      return true;
    });

    this.rebuildSummary(now);
  }

  goToWorkflow(id: number): void {
    this.router.navigate(['/workflow', id]);
  }

  private rebuildSummary(now: Date): void {
    this.totalAnalisado = this.filteredItems.length;

    this.paradosItems = this.filteredItems.filter((item) => item.diasParado >= this.paradoDias);

    if (this.orderBy === 'ATRASO') {
      this.paradosItems.sort((a, b) => b.atrasoDias - a.atrasoDias);
    } else if (this.orderBy === 'ANTIGO') {
      this.paradosItems.sort((a, b) => a.dataInicio.getTime() - b.dataInicio.getTime());
    } else {
      this.paradosItems.sort((a, b) => b.diasParado - a.diasParado);
    }

    this.totalParados = this.paradosItems.length;
    this.paradosCriticos = this.paradosItems.filter((item) => item.diasParado >= 7).length;
    this.paradosEmAtraso = this.paradosItems.filter((item) => item.atrasoDias > 0).length;
    this.maiorParado = this.paradosItems[0]?.diasParado ?? 0;
    this.mediaParado = this.calcMedia(this.paradosItems.map((item) => item.diasParado));

    this.gruposEtapa = this.buildGroupEtapa(this.paradosItems);
    this.gruposResponsavel = this.buildGroupResponsavel(this.paradosItems);
    this.selectedResponsavel = this.selectedResponsavel ?? this.gruposResponsavel[0]?.responsavel ?? null;
  }

  private buildRecords(workflows: WorkflowDetalhe[]): ParadoRecord[] {
    const now = new Date();
    return workflows.map((wf) => {
      const etapaAtual = this.getEtapaAtual(wf);
      const ultimaAtividade = this.getUltimaAtividade(wf, etapaAtual) ?? this.getDataInicio(wf);
      const diasParado = this.diffDias(ultimaAtividade, now);
      const dataFinalPrevista = this.getDataFinalPrevista(wf);
      const dataFinalReal = this.getDataFinalReal(wf);
      const atrasoDias = this.getAtrasoDias(wf, dataFinalPrevista, dataFinalReal, now);
      const emAtraso = atrasoDias > 0;
      const sinais = this.getSinaisAtividade(wf, etapaAtual);

      return {
        workflow: wf,
        osNumero: this.getOsNumero(wf),
        veiculo: [wf.placa, wf.marca, wf.modelo].filter(Boolean).join(' '),
        etapaAtual: etapaAtual?.nome ?? String(wf.etapaAtual),
        responsavel: wf.responsavelAtual || '—',
        seguradora: wf.seguradoraNome || '—',
        dataInicio: this.getDataInicio(wf),
        dataFinalPrevista,
        dataFinalReal,
        ultimaAtividade,
        diasParado,
        atrasoDias,
        emAtraso,
        sinais,
      };
    });
  }

  private buildGroupEtapa(items: ParadoRecord[]): GroupEtapa[] {
    const map = new Map<string, ParadoRecord[]>();
    items.forEach((item) => {
      if (!map.has(item.etapaAtual)) map.set(item.etapaAtual, []);
      map.get(item.etapaAtual)!.push(item);
    });
    return Array.from(map.entries())
      .map(([etapa, list]) => ({
        etapa,
        items: list,
        parados: list.length,
        criticos: list.filter((item) => item.diasParado >= 7).length,
        maxDias: Math.max(0, ...list.map((item) => item.diasParado)),
        mediaDias: this.calcMedia(list.map((item) => item.diasParado)),
        top3: [...list].sort((a, b) => b.diasParado - a.diasParado).slice(0, 3),
      }))
      .sort((a, b) => b.criticos - a.criticos);
  }

  private buildGroupResponsavel(items: ParadoRecord[]): GroupResponsavel[] {
    const map = new Map<string, ParadoRecord[]>();
    items.forEach((item) => {
      if (!map.has(item.responsavel)) map.set(item.responsavel, []);
      map.get(item.responsavel)!.push(item);
    });
    return Array.from(map.entries())
      .map(([responsavel, list]) => ({
        responsavel,
        items: list,
        parados: list.length,
        criticos: list.filter((item) => item.diasParado >= 7).length,
        etapaTop: this.getEtapaTop(list),
      }))
      .sort((a, b) => b.criticos - a.criticos);
  }

  private getEtapaTop(items: ParadoRecord[]): string {
    const map = new Map<string, number>();
    items.forEach((item) => {
      map.set(item.etapaAtual, (map.get(item.etapaAtual) ?? 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—';
  }

  getStatusBadge(item: ParadoRecord): string {
    if (item.diasParado >= 7) return 'critico';
    if (item.diasParado >= this.paradoDias) return 'alerta';
    return 'ok';
  }

  get selectedResponsavelItems(): ParadoRecord[] {
    if (!this.selectedResponsavel) return [];
    return this.gruposResponsavel.find((group) => group.responsavel === this.selectedResponsavel)?.items ?? [];
  }

  private getUltimaAtividade(wf: WorkflowDetalhe, etapaAtual?: WorkflowEtapa): Date | null {
    const dates: Date[] = [];
    if (etapaAtual?.inicio) {
      dates.push(this.toDate(etapaAtual.inicio));
    }
    const historico = wf.historico?.map((item) => this.toDate(item.dataHora)).filter(Boolean) as Date[];
    const anexos = etapaAtual?.anexos?.map((item) => this.toDate(item.dataHora)).filter(Boolean) as Date[];
    const comunicacoes = wf.comunicacoes?.map((item) => this.toDate(item.dataHora)).filter(Boolean) as Date[];
    if (historico?.length) dates.push(...historico);
    if (anexos?.length) dates.push(...anexos);
    if (comunicacoes?.length) dates.push(...comunicacoes);
    if (wf.ultimoContatoEm) dates.push(this.toDate(wf.ultimoContatoEm));
    if (!dates.length) return null;
    return new Date(Math.max(...dates.map((date) => date.getTime())));
  }

  private getSinaisAtividade(wf: WorkflowDetalhe, etapaAtual?: WorkflowEtapa) {
    const checklist = Boolean(etapaAtual?.checklist?.some((item) => item.ok));
    const comentario = Boolean(etapaAtual?.observacoes?.length);
    const anexo = Boolean(etapaAtual?.anexos?.length);
    const comunicacao = Boolean(wf.comunicacoes?.length);
    return { checklist, comentario, anexo, comunicacao };
  }

  private getEtapaAtual(wf: WorkflowDetalhe): WorkflowEtapa | undefined {
    return wf.etapas.find((item) => item.tipo === wf.etapaAtual);
  }

  private getDataInicio(wf: WorkflowDetalhe): Date {
    return wf.criadoEm instanceof Date ? wf.criadoEm : new Date(wf.criadoEm);
  }

  private getDataFinalPrevista(wf: WorkflowDetalhe): Date | null {
    if (wf.dataPrevistaEntrega) {
      return wf.dataPrevistaEntrega instanceof Date
        ? wf.dataPrevistaEntrega
        : new Date(wf.dataPrevistaEntrega);
    }
    const dates = wf.etapas
      .map((etapa) => etapa.previstaFim)
      .filter((date): date is Date => Boolean(date));
    if (!dates.length) return null;
    return new Date(Math.max(...dates.map((date) => date.getTime())));
  }

  private getDataFinalReal(wf: WorkflowDetalhe): Date | null {
    const finalizado = wf.etapas.find((etapa) => etapa.tipo === WorkflowEtapaTipo.FINALIZADO);
    if (finalizado?.fim) {
      return finalizado.fim instanceof Date ? finalizado.fim : new Date(finalizado.fim);
    }
    return null;
  }

  private isFinalizado(wf: WorkflowDetalhe): boolean {
    return wf.etapaAtual === WorkflowEtapaTipo.FINALIZADO || Boolean(this.getDataFinalReal(wf));
  }

  private getAtrasoDias(
    wf: WorkflowDetalhe,
    prevista: Date | null,
    real: Date | null,
    now: Date
  ): number {
    if (!prevista) return 0;
    if (real && real > prevista) {
      return this.diffDias(prevista, real);
    }
    if (!real && now > prevista) {
      return this.diffDias(prevista, now);
    }
    return 0;
  }

  private buildList(getter: (wf: WorkflowDetalhe) => string | undefined): string[] {
    const set = new Set<string>();
    this.workflows.forEach((wf) => {
      const value = getter(wf);
      if (value && value.trim()) {
        set.add(value.trim());
      }
    });
    return Array.from(set).sort();
  }

  private buildEtapas(workflows: WorkflowDetalhe[]): string[] {
    const set = new Set<string>();
    workflows.forEach((wf) => {
      const etapa = this.getEtapaAtual(wf);
      if (etapa?.nome) set.add(etapa.nome);
    });
    return Array.from(set).sort();
  }

  private getOsNumero(wf: WorkflowDetalhe): string {
    return wf.osNumero || `OS-${String(wf.id).padStart(6, '0')}`;
  }

  private diffDias(start: Date, end: Date): number {
    return Math.max(0, Math.floor((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)));
  }

  private toDate(value: Date | string): Date {
    return value instanceof Date ? value : new Date(value);
  }

  private toDateInput(date: Date): string {
    return date.toISOString().slice(0, 10);
  }

  private calcMedia(values: number[]): number {
    if (!values.length) return 0;
    return Math.round(values.reduce((acc, value) => acc + value, 0) / values.length);
  }
}

